import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  FormControl,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTranslation } from "react-i18next";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import ReportsLeftBar from "./ReportsLeftBar";
import ReportEditor from "./ReportEditor";

function WriteReportView() {
  const { t } = useTranslation();
  const userState = useSelector((state) => state.user);

  const [typeActivities, setTypeActivities] = useState([]);
  const [typeActivity, setTypeActivity] = useState(null);

  const [restaurants, setRestaurants] = useState([]);
  const [restaurant, setRestaurant] = useState(null);

  const [reports, setReports] = useState([]);
  const [report, setReport] = useState(null);
  const [reportById, setReportById] = useState(null);

  // ✅ Date du jour par défaut
  const [DateEvenement, setDateEvenement] = useState(dayjs());

  /* =========================
      FETCH INITIAL DATA
  ========================== */
  useEffect(() => {
    const fetchActivities = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/GetActivities`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );
      setTypeActivities(await res.json());
    };

    const fetchRestaurants = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetRestaurantsByUser`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );

      const data = await res.json();
      const headOffice = {
        noEmplacement: 1,
        nomEmplacement: "Bureau Chef / Head Office",
      };

      setRestaurants([headOffice, ...data]);
    };

    const fetchReportsByUser = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/GetReportsByUsername/${userState.username}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );
      setReports(await res.json());
    };

    fetchActivities();
    fetchRestaurants();
    fetchReportsByUser();
  }, [userState.token, userState.username]);

  /* =========================
      LOAD REPORT
  ========================== */
  useEffect(() => {
    if (!report) return;

    const fetchReport = async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/GetUncompletedReport/${report.idActivite}/${report.idTypeActivite}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );

      const data = await res.json();

      setTypeActivity(
        typeActivities.find((a) => a.id === data.idTypeActivite)
      );

      const resto = restaurants.find(
        (r) => r.noEmplacement === data.idEmplacement
      );

      setRestaurant(resto?.nomEmplacement || null);
      setDateEvenement(dayjs(data.dateEvenement || dayjs()));
      setReportById(data);
    };

    fetchReport();
  }, [report, typeActivities, restaurants, userState.token]);

  /* =========================
      HANDLERS
  ========================== */
  const handleChangeTypeActivities = (_, newValue) => {
    const selected = typeActivities.find(
      (a) =>
        (userState.lang === "EN" ? a.textEn : a.textFr) === newValue
    );
    setTypeActivity(selected || null);
  };

  const handleChangeRestaurant = (_, newValue) => {
    setRestaurant(newValue);
  };

  /* =========================
      RENDER
  ========================== */
  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        {/* LEFT BAR */}
        <Box sx={{ minWidth: "20%" }}>
          <ReportsLeftBar reports={reports} setReport={setReport} />
        </Box>

        {/* MAIN CONTENT */}
        <Box
          sx={{
            ml: 2,
            width: "80%",
            mt: 4,
          }}
        >
          {/* TITLE */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {t("ReportDetailslbl")}
              </Typography>
            </Box>
            <Divider sx={{ mt: 1 }} />
          </Box>

          {/* FORM CARD */}
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              padding: 3,
              boxShadow: "0px 2px 10px rgba(0,0,0,0.08)",
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              {/* TYPE */}
              <FormControl fullWidth>
                <Autocomplete
                  options={typeActivities.map((a) =>
                    userState.lang === "EN" ? a.textEn : a.textFr
                  )}
                  value={
                    typeActivity
                      ? userState.lang === "EN"
                        ? typeActivity.textEn
                        : typeActivity.textFr
                      : null
                  }
                  onChange={handleChangeTypeActivities}
                  renderInput={(params) => (
                    <TextField {...params} label={t("Report Type")} />
                  )}
                />
              </FormControl>

              {/* LOCATION */}
              <FormControl fullWidth>
                <Autocomplete
                  options={restaurants.map((r) => r.nomEmplacement)}
                  value={restaurant}
                  onChange={handleChangeRestaurant}
                  renderInput={(params) => (
                    <TextField {...params} label={t("Location")} />
                  )}
                />
              </FormControl>

              {/* DATE */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t("EventDate")}
                  value={DateEvenement}
                  format="YYYY/MM/DD"
                  onChange={(newValue) => setDateEvenement(newValue)}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          {/* EDITOR */}
          <ReportEditor
            key={report ? report.id : "new"}
            report={reportById}
            typeActivity={typeActivity}
            restaurant={restaurant}
            DateEvenement={DateEvenement}   // ✅
            setRestaurant={setRestaurant}
            setTypeActivity={setTypeActivity}
            setReport={setReportById}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default WriteReportView;